import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { regionAPI } from '../api/regionAPI';
import { parseCSV } from '../utils/csv';
import { GoogleMap, Polygon } from '@react-google-maps/api';
import { useGoogleMapsLoader } from '../hooks/useGoogleMapsLoader';

const DEFAULT_COLOR = '#34A853';

const THUMBNAIL_STYLE = { width: '100%', height: '144px' };
const THUMBNAIL_OPTIONS = {
    disableDefaultUI: true,
    gestureHandling: 'none',
    clickableIcons: false,
};

function RegionMapThumbnail({ boundary, color }) {
    const coords = typeof boundary === 'string' ? JSON.parse(boundary) : boundary;

    const onLoad = useCallback((map) => {
        const bounds = new window.google.maps.LatLngBounds();
        coords.forEach((c) => bounds.extend(c));
        map.fitBounds(bounds, 20);
    }, [coords]);

    return (
        <GoogleMap
            mapContainerStyle={THUMBNAIL_STYLE}
            options={THUMBNAIL_OPTIONS}
            onLoad={onLoad}
        >
            <Polygon
                paths={coords}
                options={{
                    fillColor: color || DEFAULT_COLOR,
                    fillOpacity: 0.3,
                    strokeColor: color || DEFAULT_COLOR,
                    strokeWeight: 2,
                }}
            />
        </GoogleMap>
    );
}

export function Regions() {
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    const { isLoaded: mapsLoaded } = useGoogleMapsLoader();

    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [showImport, setShowImport] = useState(false);
    const [csvRows, setCsvRows] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isAdmin) fetchRegions();
    }, [isAdmin]);

    const fetchRegions = async () => {
        setLoading(true);
        try {
            const res = await regionAPI.getRegions();
            setRegions(res.data.data.items || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load regions');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this region?')) return;
        try {
            await regionAPI.deleteRegion(id);
            setSuccess('Region deleted');
            fetchRegions();
        } catch (err) {
            setError('Failed to delete region');
        }
    };

    const handleExportCSV = () => {
        const csvField = (val) => {
            if (val == null) return '';
            const str = String(val);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) return '"' + str.replace(/"/g, '""') + '"';
            return str;
        };
        const header = 'name,color,boundary';
        const rows = regions.map((r) => {
            const boundary = r.boundary ? JSON.stringify(typeof r.boundary === 'string' ? JSON.parse(r.boundary) : r.boundary) : '';
            return [csvField(r.name), csvField(r.color || ''), csvField(boundary)].join(',');
        });
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'regions.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => { setCsvRows(parseCSV(evt.target.result)); setImportResult(null); };
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (csvRows.length === 0) return;
        setImporting(true);
        setImportResult(null);
        setError(null);
        let imported = 0;
        let failed = 0;
        const errors = [];
        for (let i = 0; i < csvRows.length; i++) {
            const row = csvRows[i];
            if (!row.name) { failed++; errors.push({ row: i + 1, error: 'Missing name' }); continue; }
            try {
                const payload = { name: row.name, color: row.color || DEFAULT_COLOR };
                if (row.boundary) payload.boundary = JSON.parse(row.boundary);
                await regionAPI.createRegion(payload);
                imported++;
            } catch (err) {
                failed++;
                errors.push({ row: i + 1, error: err.response?.data?.message || err.message || 'Failed' });
            }
        }
        setImportResult({ imported, failed, errors });
        if (imported > 0) { setSuccess(`Imported ${imported} region(s).${failed > 0 ? ` ${failed} failed.` : ''}`); fetchRegions(); }
        else if (failed > 0) setError(`All ${failed} row(s) failed. See details below.`);
        setImporting(false);
    };

    const handleCloseImport = () => {
        setShowImport(false);
        setCsvRows([]);
        setImportResult(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    if (!isAdmin) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] p-4">
                <Card>
                    <CardBody>
                        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Access Denied</h2>
                        <p className="text-sm text-slate-500 mb-4">Only administrators can manage regions.</p>
                        <Button onClick={() => navigate('/dashboard')}>Back</Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Regions</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Manage regions used by leaders and members</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" onClick={() => navigate('/dashboard')}>Back</Button>
                    <Button variant="secondary" onClick={handleExportCSV} disabled={regions.length === 0}>Export CSV</Button>
                    <Button variant="secondary" onClick={() => { setShowImport(!showImport); if (showImport) handleCloseImport(); }}>
                        {showImport ? 'Cancel Import' : 'Import CSV'}
                    </Button>
                    <Button onClick={() => navigate('/regions/new')}>+ Add Region</Button>
                </div>
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            {showImport && (
                <Card className="mb-4">
                    <CardHeader>
                        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Import Regions from CSV</h2>
                    </CardHeader>
                    <CardBody>
                        <p className="text-sm text-slate-500 mb-3">
                            CSV format: <code className="text-xs bg-slate-100 dark:bg-slate-700 px-1 py-0.5 rounded">name,color,boundary</code><br />
                            Only <strong>name</strong> is required. Boundary is a JSON array of coordinates.
                        </p>
                        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} className="mb-3 text-sm" />
                        {csvRows.length > 0 && (
                            <>
                                <div className="max-h-[300px] overflow-auto mb-3 rounded-lg border border-slate-200 dark:border-slate-700">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-50 dark:bg-slate-800/50">
                                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">#</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">Name</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">Color</th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">Boundary</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {csvRows.map((row, i) => (
                                                <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50">
                                                    <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300">{i + 1}</td>
                                                    <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300">{row.name || '-'}</td>
                                                    <td className="px-3 py-2 text-sm">
                                                        {row.color ? (
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <span className="inline-block w-3 h-3 rounded-full border border-slate-300" style={{ background: row.color }} />
                                                                {row.color}
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300">{row.boundary ? 'Yes' : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="flex gap-3">
                                    <Button onClick={handleImport} disabled={importing}>
                                        {importing ? 'Importing...' : `Import ${csvRows.length} Row(s)`}
                                    </Button>
                                    <Button variant="secondary" onClick={handleCloseImport}>Cancel</Button>
                                </div>
                            </>
                        )}
                        {importResult && (
                            <div className={`mt-3 p-3 rounded-lg border ${importResult.failed > 0 ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800' : 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800'}`}>
                                <p className="font-semibold text-sm mb-1">Result: {importResult.imported} imported, {importResult.failed} failed</p>
                                {importResult.errors && importResult.errors.length > 0 && (
                                    <ul className="max-h-[200px] overflow-auto text-xs text-red-700 dark:text-red-400 list-disc pl-5 space-y-0.5">
                                        {importResult.errors.map((e, i) => (<li key={i}>Row {e.row}: {e.error}</li>))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}

            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">Regions ({regions.length})</h2>

            {loading ? (
                <p className="text-center text-slate-500 py-6">Loading...</p>
            ) : regions.length === 0 ? (
                <p className="text-center text-slate-500 py-6">No regions defined</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {regions.map((r) => {
                        const hasBoundary = !!r.boundary;
                        return (
                            <div key={r.id} className="card-base rounded-xl overflow-hidden">
                                {/* Map thumbnail */}
                                {mapsLoaded && hasBoundary ? (
                                    <div className="h-36 rounded-t-xl overflow-hidden">
                                        <RegionMapThumbnail boundary={r.boundary} color={r.color || DEFAULT_COLOR} />
                                    </div>
                                ) : (
                                    <div className="h-36 rounded-t-xl bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center gap-1.5">
                                        <svg className="w-8 h-8 text-slate-300 dark:text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                                        </svg>
                                        <span className="text-xs text-slate-400 dark:text-slate-500">No boundary defined</span>
                                    </div>
                                )}

                                {/* Card body */}
                                <div className="p-4 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-3.5 h-3.5 rounded-full border border-slate-300 dark:border-slate-600 flex-shrink-0" style={{ background: r.color || DEFAULT_COLOR }} />
                                        <Link to={`/regions/${r.id}`} className="text-accent-500 hover:text-accent-600 font-medium text-sm truncate">{r.name}</Link>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500 font-medium">Leaders</span>
                                        <span className="text-slate-700 dark:text-slate-300 text-right truncate ml-2">{(r.leaders || []).map(l => l.name).join(', ') || '-'}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500 font-medium">Assistants</span>
                                        <span className="text-slate-700 dark:text-slate-300 text-right truncate ml-2">{(r.assistants || []).map(a => a.name).join(', ') || '-'}</span>
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                        <Button size="sm" variant="secondary" className="flex-1" onClick={() => navigate(`/regions/${r.id}`)}>Edit</Button>
                                        <Button size="sm" variant="danger" className="flex-1" onClick={() => handleDelete(r.id)}>Delete</Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default Regions;
